import { inject } from "@angular/core";
import {
  HttpErrorResponse,
  HttpInterceptorFn,
  HttpRequest,
  HttpHandlerFn,
  HttpEvent,
} from "@angular/common/http";
import { OKTA_AUTH } from "@okta/okta-angular";
import type OktaAuth from "@okta/okta-auth-js";
import {
  Observable,
  catchError,
  defer,
  from,
  shareReplay,
  switchMap,
  throwError,
} from "rxjs";
import { environment } from "../../../environments/environment";
import { AuthStore } from "../auth/auth.store";
import { isE2EBypassActive } from "../auth/e2e-bypass";

/**
 * Attach the Okta access token to requests that target our own
 * backends — Synapse, Analytics, or SimpleSchedule. Any other URL
 * (external CDNs, third-party analytics, etc.) goes through without
 * the bearer header so the token cannot leak cross-origin.
 *
 * On a 401 response, renew the access token once and retry the same
 * request transparently. A second 401 propagates to errorInterceptor
 * for the "session expired" toast.
 *
 * Parallel 401s (e.g. six requests fail at once because the token just
 * expired) are deduplicated: all of them share the same in-flight renew
 * via `shareReplay(1)` instead of each triggering its own `tokenManager.renew`.
 */

let renewInFlight$: Observable<unknown> | null = null;

function sharedRenew(okta: OktaAuth): Observable<unknown> {
  if (!renewInFlight$) {
    renewInFlight$ = defer(() => from(okta.tokenManager.renew("accessToken"))).pipe(
      shareReplay({ bufferSize: 1, refCount: false }),
      catchError((err) => {
        renewInFlight$ = null;
        return throwError(() => err);
      }),
    );
    // Clear the gate on success on the next microtask so subsequent
    // 401s (minutes later) start a fresh renew.
    renewInFlight$.subscribe({
      next: () => queueMicrotask(() => (renewInFlight$ = null)),
      error: () => {
        /* handled above */
      },
    });
  }
  return renewInFlight$;
}

export const authInterceptor: HttpInterceptorFn = (req, next) => {
  const authStore = inject(AuthStore);
  // inject() must be called synchronously during interceptor setup, so
  // resolve OKTA_AUTH unconditionally. It is still present under E2E
  // bypass (the OktaAuthModule always provides it) — we just never call
  // tokenManager.renew() on it in that mode.
  const okta = inject<OktaAuth>(OKTA_AUTH);
  const token = authStore.getToken();

  if (!token || !isOurBackend(req.url)) {
    return next(req);
  }

  if (isE2EBypassActive()) {
    return sendWithToken(req, next, token);
  }

  return sendWithToken(req, next, token).pipe(
    catchError((err) => {
      if (err instanceof HttpErrorResponse && err.status === 401) {
        return sharedRenew(okta).pipe(
          switchMap(() => {
            const fresh = authStore.getToken();
            if (!fresh) return throwError(() => err);
            return sendWithToken(req, next, fresh);
          }),
          catchError(() => throwError(() => err)),
        );
      }
      return throwError(() => err);
    }),
  );
};

function sendWithToken(
  req: HttpRequest<unknown>,
  next: HttpHandlerFn,
  token: string,
): Observable<HttpEvent<unknown>> {
  return next(
    req.clone({
      setHeaders: { Authorization: `Bearer ${token}` },
    }),
  );
}

function isOurBackend(url: string): boolean {
  const allowed = [
    environment.SYNAPSE_URL,
    environment.ANALYTICS_URL,
    environment.SIMPLE_SCHEDULE,
  ].filter((u): u is string => typeof u === "string" && u.length > 0);
  return allowed.some((prefix) => url.startsWith(prefix));
}
